// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { createFileRoute, Link, useParams } from '@tanstack/react-router';

// material ui
import { Alert, Box, Button, Stack } from '@mui/material';

// api
import { useGetGeneralQosGeneralQosAccountIdGetQuery } from '@/api/generated/serviceSystemMonitoring/endpoints/serviceSystemMonitoring';

// shared components
import PageContainer from '@/components/layout/PageContainer';
import PageHeader from '@/components/layout/PageHeader';
import LinearLoadingIndicator from '@/components/ui/progress/LinearLoadingIndicator';

// components
import QualityOfServiceDataGrid from '@/features/operations/quality-of-service/QualityOfServiceDataGrid';

export const Route = createFileRoute(
  '/$accountId/_admin/operations/quality-of-service/',
)({
  component: RouteComponent,
  staticData: { crumb: 'glossary:qualityOfService' },
});

function RouteComponent() {
  const { accountId } = useParams({ strict: false });
  const { t } = useTranslation('qualityOfService');
  const accountIdAsNumber = Number(accountId);

  const {
    data: qosResponse,
    isLoading,
    isError,
    error,
  } = useGetGeneralQosGeneralQosAccountIdGetQuery(
    { accountId: accountIdAsNumber },
    { skip: Number.isNaN(accountIdAsNumber) },
  );

  const rows = (qosResponse?.records ?? []).map((record) => ({
    id: `${record.unitId}-${record.unitTypeInputId}-${record.dataType}-${record.alarmConfigId}`,
    ...record,
  }));

  return (
    <>
      <LinearLoadingIndicator isLoading={isLoading} />
      <PageContainer maxWidth="xl" sx={{ py: 2 }}>
        <PageHeader title="Quality of Service" disableArrow>
          <Link to={`/$accountId/alarms/configuration/create/25`}>
            <Button variant="contained">
              {t('actions.create', { ns: 'common' })}
            </Button>
          </Link>
        </PageHeader>
        <Stack spacing={2}>
          {!isLoading && isError ? (
            <Alert
              severity="error"
              sx={{ border: '1px solid', borderColor: 'error.main' }}
            >
              {t('fetchError', {
                defaultValue: 'Failed to fetch Quality of Service data.',
              })}
              <Box
                sx={{ mt: 1, fontSize: '0.875rem', color: 'text.secondary' }}
              >
                {error instanceof Error ? error.message : JSON.stringify(error)}
              </Box>
            </Alert>
          ) : (
            <QualityOfServiceDataGrid rows={rows} />
          )}
        </Stack>
      </PageContainer>
    </>
  );
}
